import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePushNotifications from "@/hooks/usePushNotifications";
import {
  X, Loader2, KeyRound, CheckCircle2, BellRing, BellOff, Monitor, LogOut,
  Clock4, Globe,
} from "lucide-react";

const TIMEZONES = [
  { v: "America/New_York", l: "Eastern (New York)" },
  { v: "America/Chicago", l: "Central (Chicago)" },
  { v: "America/Denver", l: "Mountain (Denver)" },
  { v: "America/Los_Angeles", l: "Pacific (Los Angeles)" },
  { v: "America/Anchorage", l: "Alaska (Anchorage)" },
  { v: "Pacific/Honolulu", l: "Hawaii (Honolulu)" },
];

/**
 * "My Account" modal. Three sections:
 *   1. Change password
 *   2. Push notifications (PWA / web push)
 *   3. Sessions — current session + "Sign out everywhere"
 */
export default function ChangePassword({ onClose }) {
  const { user, logout } = useAuth();

  // ---- Section 1: password change ---------------------------------------
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const lengthOk = next.length >= 8;
  const matches = next.length > 0 && next === confirm;
  const differs = next.length === 0 || next !== current;
  const canSubmit = current && lengthOk && matches && differs && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setOk("Password updated successfully. Use your new password the next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // ---- Section 2: push notifications ------------------------------------
  const push = usePushNotifications();

  // ---- Section 2b: timezone + quiet hours preferences -------------------
  const [tz, setTz] = useState(user?.timezone || "America/New_York");
  const [quietOn, setQuietOn] = useState(user?.quiet_hours_enabled !== false);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  useEffect(() => {
    setTz(user?.timezone || "America/New_York");
    setQuietOn(user?.quiet_hours_enabled !== false);
  }, [user?.timezone, user?.quiet_hours_enabled]);

  const savePrefs = async () => {
    if (!user?.id) return;
    setPrefsBusy(true); setPrefsSaved(false);
    try {
      await api.patch(`/users/me/preferences`, {
        timezone: tz,
        quiet_hours_enabled: quietOn,
      });
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2500);
    } catch (_) {
      // quiet on failure — validation errors are rare for a fixed enum
    } finally {
      setPrefsBusy(false);
    }
  };

  // ---- Section 3: sessions ----------------------------------------------
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [signingOutAll, setSigningOutAll] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/auth/sessions").then((r) => {
      if (alive) { setSessions(r.data || []); setLoadingSessions(false); }
    }).catch(() => alive && setLoadingSessions(false));
    return () => { alive = false; };
  }, []);

  const signOutEverywhere = async () => {
    if (!user?.id) return;
    if (!window.confirm("This will sign you out on all devices. Continue?")) return;
    setSigningOutAll(true);
    try {
      await api.post(`/users/${user.id}/force-logout`);
    } catch (_) {
      // fall through — we still sign out locally
    }
    await logout();
    setSigningOutAll(false);
  };

  const formatLast = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="accp-modal-card accp-modal-card-lg space-y-6"
        data-testid="change-password-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label">Account security</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              My account
            </h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="change-password-close">
            <X size={18} />
          </button>
        </div>

        {/* --- Change password form --- */}
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="accp-label flex items-center gap-2 text-primary">
            <KeyRound size={13} /> Change password
          </div>

          <label className="block">
            <span className="accp-label">Current password</span>
            <input
              type="password"
              className="accp-input mt-1"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="current-password-input"
            />
          </label>

          <label className="block">
            <span className="accp-label">New password</span>
            <input
              type="password"
              className="accp-input mt-1"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              data-testid="new-password-input"
            />
            <span className={`text-[11px] mt-1 inline-block ${lengthOk ? "text-[#4A7C1E]" : "text-muted-foreground"}`}>
              Must be at least 8 characters.
            </span>
          </label>

          <label className="block">
            <span className="accp-label">Confirm new password</span>
            <input
              type="password"
              className="accp-input mt-1"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="confirm-password-input"
            />
            {confirm.length > 0 && !matches && (
              <span className="text-[11px] mt-1 inline-block text-destructive">Passwords do not match.</span>
            )}
            {next.length > 0 && !differs && (
              <span className="text-[11px] mt-1 inline-block text-destructive">New password must differ from current.</span>
            )}
          </label>

          {err && (
            <div className="rounded-xl p-3 text-sm compliance-expired" data-testid="change-password-error">
              {err}
            </div>
          )}
          {ok && (
            <div className="rounded-xl p-3 text-sm compliance-valid flex items-center gap-2"
                 data-testid="change-password-success">
              <CheckCircle2 size={16} /> {ok}
            </div>
          )}

          <button
            type="submit"
            className="accp-btn-primary w-full"
            disabled={!canSubmit}
            data-testid="change-password-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busy ? "Updating…" : "Change password"}
          </button>
        </form>

        {/* --- Push notifications --- */}
        <section className="pt-5 border-t border-border space-y-3" data-testid="push-section">
          <div className="accp-label flex items-center gap-2 text-primary">
            <BellRing size={13} /> Push notifications
          </div>
          {!push.env.supported ? (
            <p className="text-xs text-muted-foreground">
              This browser does not support web push. Use the latest Chrome, Edge, Firefox, or iOS Safari 16.4+ (installed to Home Screen).
            </p>
          ) : push.env.needsInstallForIOS ? (
            <div className="rounded-xl bg-[#FFF8E6] border border-[#FFE4A0] text-[#7A5A00] p-3 text-xs"
                 data-testid="push-ios-install-banner">
              Install ACCP to your home screen to enable push notifications on iOS.
              Tap <span className="font-semibold">Share → Add to Home Screen</span>, then open the installed app.
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-accent/40 p-3">
              <div className="text-xs text-primary">
                {push.subscribed
                  ? <>Notifications are <span className="font-semibold">ON</span> for this device.</>
                  : <>Enable alerts for missed clock-ins, new shifts, expiring credentials, and critical incidents.</>}
              </div>
              {push.subscribed ? (
                <button onClick={push.disable} disabled={push.busy}
                        className="accp-btn-ghost text-xs" data-testid="push-disable-btn">
                  {push.busy ? <Loader2 size={12} className="animate-spin" /> : <BellOff size={12} />}
                  Turn off
                </button>
              ) : (
                <button onClick={push.enable} disabled={push.busy}
                        className="accp-btn-primary text-xs !py-1.5 !px-3" data-testid="push-enable-btn">
                  {push.busy ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
                  Enable
                </button>
              )}
            </div>
          )}
          {push.error && (
            <div className="text-[11px] text-destructive" data-testid="push-error">{push.error}</div>
          )}
        </section>

        {/* --- Timezone + quiet hours --- */}
        <section className="pt-5 border-t border-border space-y-3" data-testid="prefs-section">
          <div className="accp-label flex items-center gap-2 text-primary">
            <Globe size={13} /> Timezone &amp; quiet hours
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Your timezone — used for quiet hours and scheduled alerts</span>
            <select
              className="accp-input mt-1"
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              data-testid="timezone-select"
            >
              {TIMEZONES.map((t) => (
                <option key={t.v} value={t.v}>{t.l}</option>
              ))}
            </select>
          </label>
          <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl bg-accent/40 p-3">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={quietOn}
              onChange={(e) => setQuietOn(e.target.checked)}
              data-testid="quiet-hours-toggle"
            />
            <span className="text-xs text-primary">
              <span className="font-semibold flex items-center gap-1.5"><Clock4 size={13} /> Pause non-urgent push notifications between 10pm–6am</span>
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                Critical incidents and overtime alerts will still come through immediately.
              </span>
            </span>
          </label>          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={savePrefs}
              disabled={prefsBusy}
              className="accp-btn-primary text-xs !py-1.5 !px-3"
              data-testid="save-prefs-btn"
            >
              {prefsBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Save preferences
            </button>
            {prefsSaved && (
              <span className="text-[11px] text-[#4A7C1E]" data-testid="prefs-saved">
                Saved
              </span>
            )}
          </div>
        </section>

        {/* --- Sessions --- */}
        <section className="pt-5 border-t border-border space-y-3" data-testid="sessions-section">
          <div className="accp-label flex items-center gap-2 text-primary">
            <Monitor size={13} /> Sessions
          </div>
          {loadingSessions ? (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-xs text-muted-foreground">No active sessions.</div>
          ) : (
            <ul className="space-y-2" data-testid="sessions-list">
              {sessions.map((s, idx) => (
                <li key={idx} className="rounded-xl border border-border p-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-primary">
                      Current session
                    </span>
                    {s.current && (
                      <span className="inline-flex px-2 py-0.5 rounded-full compliance-valid text-[10px] font-bold tracking-wider uppercase">
                        this device
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Last login: {formatLast(s.last_login_at)}
                  </div>
                  <div className="text-muted-foreground">IP: {s.ip || "—"}</div>
                  <div className="text-muted-foreground truncate" title={s.device}>
                    {s.device}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={signOutEverywhere}
            disabled={signingOutAll}
            className="accp-btn-outline w-full text-destructive border-destructive/30 hover:bg-destructive/5"
            data-testid="sign-out-everywhere-btn"
          >
            {signingOutAll ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
            Sign out everywhere
          </button>
          <p className="text-[11px] text-muted-foreground">
            Ends every active session for your account, including this device. You will need to sign in again with your current password.
          </p>
        </section>
      </div>
    </div>
  );
}
